import React, { ChangeEvent, forwardRef, useEffect, useRef, useState } from 'react'

import Fab from '@mui/material/Fab'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'

import { useTranslation } from 'react-i18next'
import MedicalVideo, { MedicalVideoProps, MedicalVideoHandle } from '@/components/group/MedicalVideo'

import { DiagnosticRTCServiceType, useDiagnosticRTC } from '@/providers/diagnosticRTC/DiagnosticRTCProvider'

import DiagnosticRTCServiceSwitch from './DiagnosticRTCServiceSwitch'
import QrCodeDialog from './QrCodeDialog'
import { Alert } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import LoadingSwitch from '@/components/ui/switchs/LoadingSwitch'

export type DiagnosticMedicalVideoProps = Omit<
  MedicalVideoProps,
  'isStreamer' | 'medicalCameras' | 'videoMediaDevices' | 'onScreenshot' | 'selectedDeviceId' | 'stream' | 'renderVideo'
> & {
  disableStreamOnMobile?: boolean
  onScreenshot?: (file: File) => void
  onToggleStreamingOnMobile?: (isStreamingOnMobile: boolean) => void
  streamingOnMobileLinkQrCode?: string
  isLoadingQrCodeMobile?: boolean
  screenshotCount?: number
}

export const DiagnosticMedicalVideo = forwardRef<MedicalVideoHandle, DiagnosticMedicalVideoProps>(function DiagnosticMedicalVideo(props, ref) {
  const {
    disableStreamOnMobile = false,
    videoHeight = '100%',
    videoWidth = 'auto',
    onScreenshot,
    onToggleStreamingOnMobile,
    streamingOnMobileLinkQrCode,
    isLoadingQrCodeMobile,
    screenshotCount,
    ...rest
  } = props
  const {
    activeDiagnosticRTCService,
    captureScreen,
    changeDiagnosticRTCService,
    devices,
    diagnosticRTCServices,
    isStreamer,
    refreshConnection,
    renderVideo,
    selectDevice,
    selectResolution,
    selectedDeviceId,
    shareScreen,
    stopVideo,
    stream,
  } = useDiagnosticRTC()

  const { t } = useTranslation()
  const autoSelectDeviceRef = useRef<boolean>(true)

  const { medicalCameras, videoMediaDevices } = devices

  const [isStreamingOnMobile, setIsStreamingOnMobile] = useState(false)
  const [qrCodeDialogOpen, setQrCodeDialogOpen] = useState<boolean>(false)

  useEffect(() => {
    if (!!selectedDeviceId || !autoSelectDeviceRef.current) return

    if (!!medicalCameras?.length) {
      selectDevice(medicalCameras[0].device.deviceId)
    } else if (!!videoMediaDevices.length) {
      selectDevice(videoMediaDevices[0].deviceId)
    }
    autoSelectDeviceRef.current = false
  }, [medicalCameras, selectDevice, selectedDeviceId, videoMediaDevices])

  useEffect(() => {
    autoSelectDeviceRef.current = true
  }, [activeDiagnosticRTCService])

  useEffect(() => {
    if (!autoSelectDeviceRef.current && !medicalCameras.length && !videoMediaDevices.length) {
      autoSelectDeviceRef.current = true
    }
  }, [medicalCameras, videoMediaDevices])

  const handleChangeStreamingOnMobile = (_event: ChangeEvent, checked: boolean) => {
    setIsStreamingOnMobile(checked)
    onToggleStreamingOnMobile?.(checked)
    if (checked) setQrCodeDialogOpen(true)
  }

  const handleScreenshot = () => {
    captureScreen((image: File) => {
      onScreenshot?.(image)
    })
  }

  const handleStopVideo = () => {
    stopVideo()
  }

  const [open, setOpen] = useState(!localStorage.getItem('alertClosed'))

  useEffect(() => {
    // Set the item in local storage when the alert is closed
    if (!open) {
      localStorage.setItem('alertClosed', 'true')
    }
  }, [open])

  return (
    <Stack sx={{ height: videoHeight, width: videoWidth, position: 'relative' }}>
      {/* <div>
        {open ? (
          <Alert
            // severity="warning"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setOpen(false)
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            Un pare-feu peut bloquer le serveur 1, si le médecin ne reçoit pas les données, merci d'essayer le serveur 2. Si le problème persiste contactez nous
            info@promotal-medconnect.com
          </Alert>
        ) : null}
      </div> */}
      <Stack direction="row" alignItems="center" justifyContent={!disableStreamOnMobile && isStreamer ? 'space-between' : 'end'}>
        {!disableStreamOnMobile && isStreamer && (
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={<LoadingSwitch loading={isLoadingQrCodeMobile} checked={isStreamingOnMobile} onChange={handleChangeStreamingOnMobile} size="small" />}
              label={t('component.group.medicalVideo.streamOnMobile')}
              labelPlacement="start"
            />
            {isStreamingOnMobile && (
              <IconButton onClick={() => setQrCodeDialogOpen(true)} size="small">
                <QrCodeScannerIcon fontSize="inherit" />
              </IconButton>
            )}
          </Stack>
        )}
        <DiagnosticRTCServiceSwitch onChange={changeDiagnosticRTCService} selectedService={activeDiagnosticRTCService} services={diagnosticRTCServices} />
      </Stack>
      {(activeDiagnosticRTCService === DiagnosticRTCServiceType.Peerjs || activeDiagnosticRTCService === DiagnosticRTCServiceType.Rpi) && !isStreamer && (
        <Fab color="primary" onClick={refreshConnection} size="small" sx={{ position: 'absolute', top: 53.5, left: 15, zIndex: 1 }}>
          <RefreshRoundedIcon />
        </Fab>
      )}
      <MedicalVideo
        {...rest}
        ref={ref}
        disableConnectionIndicator={!diagnosticRTCServices.includes(DiagnosticRTCServiceType.Peerjs)}
        isStreamer={isStreamer}
        medicalCameras={medicalCameras}
        onScreenshot={handleScreenshot}
        onSelectDevice={selectDevice}
        onSelectResolution={selectResolution}
        onShareScreen={shareScreen}
        onStopVideo={handleStopVideo}
        renderVideo={renderVideo}
        selectedDeviceId={selectedDeviceId || undefined}
        stream={stream}
        videoMediaDevices={videoMediaDevices}
        screenshotCount={screenshotCount}
      />
      {streamingOnMobileLinkQrCode && (
        <QrCodeDialog open={qrCodeDialogOpen} onClose={() => setQrCodeDialogOpen(false)} qrCodeValue={streamingOnMobileLinkQrCode} />
      )}
    </Stack>
  )
})

export default DiagnosticMedicalVideo
